import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { sessionQueryKey } from './session';

/**
 * Server-sent events of the shell (docs/dev/api-shell.md "Events"): one `EventSource` on `/api/events`
 * per signed-in tab. Every event invalidates the queries it concerns; components can additionally
 * subscribe to a type with `useRealtime`. The browser reconnects on its own (`retry` from the server).
 */

export type RealtimeEventType =
  | 'notification.created'
  | 'notification.read'
  | 'task.count'
  | 'task.updated'
  | 'queue.state'
  | 'contact.created'
  | 'contact.updated'
  | 'contact.merged'
  | 'deal.created'
  | 'deal.updated'
  | 'commission.updated'
  | 'reservation.updated'
  | 'self_disclosure.updated'
  | 'property.updated'
  | 'unit.updated'
  | 'appointment.updated'
  | 'event_type.updated'
  | 'availability.updated'
  | 'approval.decided'
  | 'approval.withdrawn'
  | 'message.updated'
  | 'document.updated'
  | 'connection.updated'
  | 'reminder_chain.updated'
  | 'meeting.updated'
  | 'meeting.lobby'
  | 'call.updated'
  | 'call.transcript'
  | 'call.hint'
  | 'call_list.updated'
  | 'dialer.metrics'
  | 'dialer.briefing'
  | 'financing.updated'
  | 'ticket.updated'
  | 'inbox.updated'
  | 'after_sales.updated'
  | 'portal.updated'
  | 'user.updated'
  | 'invitation.updated'
  | 'telephony.updated'
  | 'billing.updated'
  | 'organization.updated';

export interface RealtimeEvent {
  type: RealtimeEventType;
  payload: Record<string, unknown>;
}

type Handler = (event: RealtimeEvent) => void;

/**
 * Query key roots per module (docs/dev/api-*.md "realtime"): a module keys its queries under its root
 * (`['contacts']`, `['contact', id]`, `['tasks']`, …) so that an event can refresh everything it concerns.
 */
export const queryRoots = {
  session: sessionQueryKey,
  notifications: ['notifications'],
  tasks: ['tasks'],
  queue: ['queue'],
  approvals: ['approvals'],
  controlling: ['controlling'],
  contacts: ['contacts'],
  contact: ['contact'],
  appointments: ['appointments'],
  eventTypes: ['event-types'],
  availability: ['availability'],
  slots: ['slots'],
  messages: ['messages'],
  documents: ['documents'],
  selfDisclosures: ['self-disclosures'],
  connections: ['connections'],
  deals: ['deals'],
  deal: ['deal'],
  reservations: ['reservations'],
  properties: ['properties'],
  property: ['property'],
  units: ['units'],
  reminderChains: ['reminder-chains'],
  meetings: ['meetings'],
  dialer: ['dialer'],
  calls: ['calls'],
  telephony: ['telephony'],
  financings: ['financings'],
  financing: ['financing'],
  tickets: ['tickets'],
  ticket: ['ticket'],
  supportMailbox: ['support-mailbox'],
  afterSales: ['after-sales'],
  afterSalesCase: ['after-sales-case'],
  users: ['users'],
  profile: ['profile'],
  setup: ['setup'],
  invitations: ['invitations'],
  billing: ['billing'],
  organization: ['organization'],
} as const;

export const REALTIME_TYPES: readonly RealtimeEventType[] = [
  'notification.created',
  'notification.read',
  'task.count',
  'task.updated',
  'queue.state',
  'contact.created',
  'contact.updated',
  'contact.merged',
  'deal.created',
  'deal.updated',
  'commission.updated',
  'reservation.updated',
  'self_disclosure.updated',
  'property.updated',
  'unit.updated',
  'appointment.updated',
  'event_type.updated',
  'availability.updated',
  'approval.decided',
  'approval.withdrawn',
  'message.updated',
  'document.updated',
  'connection.updated',
  'reminder_chain.updated',
  'meeting.updated',
  'meeting.lobby',
  'call.updated',
  'call.transcript',
  'call.hint',
  'call_list.updated',
  'dialer.metrics',
  'dialer.briefing',
  'financing.updated',
  'ticket.updated',
  'inbox.updated',
  'after_sales.updated',
  'portal.updated',
  'user.updated',
  'invitation.updated',
  'telephony.updated',
  'billing.updated',
  'organization.updated',
];

/** Query keys refreshed per event type. */
const invalidations: Record<RealtimeEventType, readonly (readonly string[])[]> = {
  'notification.created': [queryRoots.notifications, queryRoots.session],
  'notification.read': [queryRoots.notifications, queryRoots.session],
  'task.count': [queryRoots.session, queryRoots.tasks, queryRoots.queue],
  'task.updated': [queryRoots.tasks, queryRoots.queue, queryRoots.contact, queryRoots.approvals],
  'queue.state': [queryRoots.session, queryRoots.tasks, queryRoots.queue, queryRoots.dialer],
  'contact.created': [queryRoots.contacts, queryRoots.queue],
  'contact.updated': [queryRoots.contacts, queryRoots.contact, queryRoots.tasks, queryRoots.selfDisclosures],
  'contact.merged': [queryRoots.contacts, queryRoots.contact, queryRoots.approvals],
  'deal.created': [queryRoots.contact, queryRoots.deals],
  'deal.updated': [
    queryRoots.deals,
    queryRoots.deal,
    queryRoots.contact,
    queryRoots.tasks,
    queryRoots.reservations,
  ],
  'commission.updated': [queryRoots.deals, queryRoots.deal],
  'reservation.updated': [
    queryRoots.reservations,
    queryRoots.deals,
    queryRoots.deal,
    queryRoots.contact,
    queryRoots.units,
    queryRoots.property,
    queryRoots.approvals,
  ],
  'self_disclosure.updated': [queryRoots.selfDisclosures, queryRoots.contact],
  'property.updated': [queryRoots.properties, queryRoots.property, queryRoots.units],
  'unit.updated': [queryRoots.properties, queryRoots.property, queryRoots.units],
  'appointment.updated': [
    queryRoots.appointments,
    queryRoots.slots,
    queryRoots.contact,
    queryRoots.tasks,
    queryRoots.queue,
  ],
  'event_type.updated': [queryRoots.eventTypes, queryRoots.slots],
  'availability.updated': [queryRoots.availability, queryRoots.slots],
  'approval.decided': [
    queryRoots.approvals,
    queryRoots.tasks,
    queryRoots.contact,
    queryRoots.reservations,
    queryRoots.units,
    queryRoots.deal,
  ],
  'approval.withdrawn': [queryRoots.approvals, queryRoots.tasks],
  'message.updated': [queryRoots.messages, queryRoots.contact, queryRoots.connections],
  'document.updated': [queryRoots.documents, queryRoots.contact, queryRoots.property, queryRoots.units],
  'connection.updated': [queryRoots.connections, queryRoots.availability],
  'reminder_chain.updated': [queryRoots.reminderChains, queryRoots.eventTypes],
  'meeting.updated': [queryRoots.meetings, queryRoots.appointments, queryRoots.contact],
  'meeting.lobby': [queryRoots.meetings],
  'call.updated': [queryRoots.calls, queryRoots.dialer, queryRoots.contact],
  // Live transcript and hints of the running call: consumed by `useLiveTranscript`, nothing to refetch.
  'call.transcript': [],
  'call.hint': [],
  'call_list.updated': [queryRoots.dialer],
  'dialer.metrics': [queryRoots.dialer],
  // The KI-Briefing of a contact was generated in the background (api-dialer.md „AI").
  'dialer.briefing': [queryRoots.dialer],
  'financing.updated': [queryRoots.financings, queryRoots.financing, queryRoots.deal],
  'ticket.updated': [queryRoots.tickets, queryRoots.ticket],
  'inbox.updated': [queryRoots.supportMailbox],
  'after_sales.updated': [queryRoots.afterSales, queryRoots.afterSalesCase],
  // The contact's portal card (`['contact', id, 'portal']`), its timeline and its documents.
  'portal.updated': [queryRoots.contact, queryRoots.documents],
  // Profile, avatar, signature, wizard steps, role/team/billing changes and invitation acceptances (M8).
  'user.updated': [queryRoots.users, queryRoots.profile, queryRoots.setup],
  'invitation.updated': [queryRoots.invitations],
  // The telephony journey (Einstellungen 25/26) and the billing pages (Einstellungen 18/19/27/28, M8).
  'telephony.updated': [queryRoots.telephony],
  'billing.updated': [queryRoots.billing, queryRoots.users],
  // Company, brand, legal texts, domain, modules, pipelines and the wizard steps (M8, `{ section }`).
  'organization.updated': [queryRoots.organization, queryRoots.setup],
};

const handlers = new Map<RealtimeEventType, Set<Handler>>();
let source: EventSource | null = null;
let connections = 0;

function dispatch(event: RealtimeEvent, queryClient: QueryClient): void {
  for (const key of invalidations[event.type]) void queryClient.invalidateQueries({ queryKey: [...key] });
  const list = handlers.get(event.type);
  if (!list) return;
  for (const handler of list) handler(event);
}

function parse(type: RealtimeEventType, raw: string): RealtimeEvent {
  try {
    const parsed: unknown = JSON.parse(raw);
    const payload: unknown =
      typeof parsed === 'object' && parsed !== null && 'payload' in parsed ? parsed.payload : {};
    return {
      type,
      payload: typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>) : {},
    };
  } catch {
    return { type, payload: {} };
  }
}

function open(queryClient: QueryClient): void {
  if (source !== null || typeof EventSource === 'undefined') return;
  const stream = new EventSource('/api/events');
  for (const type of REALTIME_TYPES) {
    stream.addEventListener(type, (message: MessageEvent<string>) => {
      dispatch(parse(type, message.data), queryClient);
    });
  }
  source = stream;
}

function close(): void {
  source?.close();
  source = null;
}

/** Keeps the event stream open while a signed-in shell is mounted. */
export function useRealtimeConnection(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    connections += 1;
    open(queryClient);
    return () => {
      connections -= 1;
      if (connections === 0) close();
    };
  }, [queryClient]);
}

/** Runs `handler` for every event of `type` while the component is mounted. */
export function useRealtime(type: RealtimeEventType, handler: Handler): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    const stable: Handler = (event) => handlerRef.current(event);
    const list = handlers.get(type) ?? new Set<Handler>();
    list.add(stable);
    handlers.set(type, list);
    return () => {
      list.delete(stable);
    };
  }, [type]);
}
